import { getSupabaseClient, isSupabaseConfigured } from '@/services/supabaseClient';
import { getDiscordUserId, getDiscordDisplayName } from '@/discord';
import { isDiscordActivity } from '@/utils/discordActivity';

export type AccountStatus =
  | { configured: false; state: 'offline'; email: null; hasSession: false; isDiscord: false; discordUsername: null }
  | { configured: true; state: 'anonymous'; email: null; hasSession: boolean; isDiscord: false; discordUsername: null }
  | { configured: true; state: 'signed_in'; email: string; hasSession: true; isDiscord: boolean; discordUsername: string | null };

function isPlaceholderEmail(email: string): boolean {
  return email.startsWith('discord-') && email.includes('@discord.');
}

export async function fetchAccountStatus(): Promise<AccountStatus> {
  // In Discord Activity mode, use Discord SDK state directly (no Supabase session needed)
  if (isDiscordActivity()) {
    const discordUserId = getDiscordUserId();
    const discordUsername = getDiscordDisplayName();

    if (discordUserId) {
      return {
        configured: true,
        state: 'signed_in',
        email: `discord-${discordUserId}@discord.morsehero.io`,
        hasSession: true,
        isDiscord: true,
        discordUsername,
      };
    }
    // Not authenticated with Discord yet
    return { configured: true, state: 'anonymous', email: null, hasSession: false, isDiscord: false, discordUsername: null };
  }

  if (!isSupabaseConfigured()) {
    return { configured: false, state: 'offline', email: null, hasSession: false, isDiscord: false, discordUsername: null };
  }

  const supabase = getSupabaseClient();
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    // Treat transient failures as "offline" rather than crashing the UI.
    return { configured: false, state: 'offline', email: null, hasSession: false, isDiscord: false, discordUsername: null };
  }

  const session = data.session;
  if (!session) {
    return { configured: true, state: 'anonymous', email: null, hasSession: false, isDiscord: false, discordUsername: null };
  }

  const email = session.user.email ?? null;

  if (!email) {
    return { configured: true, state: 'anonymous', email: null, hasSession: true, isDiscord: false, discordUsername: null };
  }

  // Check for Discord user via app_metadata
  const appMetadata = session.user.app_metadata ?? {};
  const discordUserId = appMetadata.discord_user_id;
  const discordUsername = appMetadata.discord_global_name ?? appMetadata.discord_username ?? null;
  const isDiscord = Boolean(discordUserId) || isPlaceholderEmail(email);

  return {
    configured: true,
    state: 'signed_in',
    email,
    hasSession: true,
    isDiscord,
    discordUsername: isDiscord ? discordUsername : null,
  };
}

export function shouldOfferSaveProgress(status: AccountStatus): boolean {
  // In Discord Activity mode, users are already authenticated via Discord
  // and their progress is saved automatically - no need to offer email OTP.
  if (isDiscordActivity()) {
    return false;
  }

  // Product rule: offer "save progress" only if the player is not already signed in.
  return status.configured && status.state !== 'signed_in';
}
